import { useState } from "react";
import { useParams } from "react-router-dom";
import useLang from "../../hooks/useLang";
import useSimpleDate from "../../hooks/useSimpleDate";
import Button from "../../components/Button";
import Input from "../forms/Input";
import { usePatchFeatureExampleMutation } from "../features/featuresSlice";
import styles from "./featuresByLabel.module.css";

const COLUMNS = [
  { key: "positive", apiLabel: "positive", langKey: "positive_features" },
  { key: "negative", apiLabel: "negative", langKey: "negative_features" },
  { key: "unassigned", apiLabel: "unassigned", langKey: "unassigned_features" },
];

const PAGE = 20;
const INITIAL_SHOWN = { positive: PAGE, negative: PAGE, unassigned: PAGE };

const FeatureRow = ({ feature, currentLabel, editing, onMove }) => {
  const lang = useLang();
  const { getSimpleDate } = useSimpleDate();
  const otherLabels = ["positive", "negative", "unassigned"].filter(
    (l) => l !== currentLabel
  );
  return (
    <div className={styles.featureRow}>
      <Button
        inline
        unpadded
        navigation
        to={`/features/${feature?.feature_id}`}
        target="_blank"
      >
        {feature?.name}
      </Button>
      {feature?.last_edit_at ? (
        <span className={styles.lastEdit}>
          {getSimpleDate(feature.last_edit_at)}
        </span>
      ) : null}
      {feature?.label_source === "report" ? (
        <span className={styles.reportMarker}>
          {lang("captions.from_report")}
        </span>
      ) : null}
      {editing ? (
        <span className={styles.actions}>
          {otherLabels.map((label) => (
            <Button
              key={label}
              inline
              unpadded
              onClick={() => onMove(feature?.feature_id, label)}
            >
              {lang(`captions.${label}`)}
            </Button>
          ))}
        </span>
      ) : null}
    </div>
  );
};

const matchesFilter = (feature, needle) =>
  !needle || feature?.name?.toLowerCase().includes(needle);

const FeaturesByLabel = ({ positive, negative, unassigned, editing }) => {
  const lang = useLang();
  const { spectrumId } = useParams();
  const [patchFeatureExample] = usePatchFeatureExampleMutation();
  const [filterText, setFilterText] = useState("");
  const [shown, setShown] = useState(INITIAL_SHOWN);
  const buckets = { positive, negative, unassigned };
  const needle = filterText.trim().toLowerCase();

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    setShown(INITIAL_SHOWN);
  };

  const handleShowMore = (key) =>
    setShown((prev) => ({ ...prev, [key]: (prev[key] || PAGE) + PAGE }));

  const handleMove = (featureId, label) => {
    if (!featureId || !spectrumId || !label) return;
    patchFeatureExample({
      params: { featureId, spectrumId },
      body: { label },
    });
  };

  return (
    <div className={styles.container}>
      <Input
        type="text"
        name="featureFilter"
        value={filterText}
        onChange={handleFilterChange}
        placeholder={lang("features.filter_features_placeholder")}
      />
      <div className={styles.columns}>
        {COLUMNS.map((col) => {
          const all = buckets[col.key] || [];
          const filtered = all.filter((feature) =>
            matchesFilter(feature, needle)
          );
          const limit = shown[col.key] ?? PAGE;
          const visible = filtered.slice(0, limit);
          const remaining = filtered.length - visible.length;
          const countText = needle
            ? `${filtered.length} / ${all.length}`
            : `${all.length}`;
          return (
            <div key={col.key} className={styles.column}>
              <div className={styles.columnHeader}>
                {`${lang(`features.${col.langKey}`)} (${countText})`}
              </div>
              <div className={styles.cards}>
                {visible.length > 0 ? (
                  visible.map((feature) => (
                    <FeatureRow
                      key={feature?.feature_id}
                      feature={feature}
                      currentLabel={col.apiLabel}
                      editing={editing}
                      onMove={handleMove}
                    />
                  ))
                ) : (
                  <div className={styles.emptyColumn}>
                    {lang("features.empty_label_group")}
                  </div>
                )}
                {remaining > 0 ? (
                  <Button inline onClick={() => handleShowMore(col.key)}>
                    {`${lang("features.show_more")} (${remaining})`}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeaturesByLabel;
